"use client";

import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { format, parseISO, isValid } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  UserRound,
  CreditCard,
  Check,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import Link from "next/link";

import { Header } from "@/components/shared/header";
import { Footer } from "@/components/shared/footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { SITE } from "@/lib/constants";

import {
  AvailabilityCalendar,
  type DateRange,
  type PricingData,
} from "@/components/booking/availability-calendar";
import {
  PriceBreakdown,
  calculatePricing,
} from "@/components/booking/price-breakdown";
import { GuestForm, type GuestFormData } from "@/components/booking/guest-form";
import { PaymentStep } from "@/components/booking/payment-step";
import { BookingConfirmation } from "@/components/booking/booking-confirmation";

const STEPS = [
  { label: "Dates", icon: CalendarDays },
  { label: "Details", icon: UserRound },
  { label: "Payment", icon: CreditCard },
] as const;

interface BookingState {
  dateRange: DateRange;
  pricingData: PricingData;
  blockedDates: Date[];
  guestData: GuestFormData | null;
  bookingId: string | null;
  bookingRef: string | null;
  orderId: string | null;
  razorpayKeyId: string | null;
  advanceAmount: number;
  totalAmount: number;
}

const formatINR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

function ProgressIndicator({
  currentStep,
  completed,
}: {
  currentStep: number;
  completed: boolean;
}) {
  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3">
      {STEPS.map((step, i) => {
        const Icon = step.icon;
        const isActive = i === currentStep;
        const isDone = i < currentStep || completed;

        return (
          <div key={step.label} className="flex items-center gap-2 sm:gap-3">
            {i > 0 && (
              <div
                className={cn(
                  "h-px w-6 sm:w-10 transition-colors duration-300",
                  isDone ? "bg-primary" : "bg-border"
                )}
              />
            )}
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={cn(
                  "flex size-10 items-center justify-center rounded-full border-2 transition-all duration-300",
                  isActive &&
                    "border-primary bg-primary text-primary-foreground scale-110",
                  isDone &&
                    !isActive &&
                    "border-primary bg-primary/20 text-primary",
                  !isActive && !isDone && "border-border text-muted-foreground"
                )}
              >
                {isDone && !isActive ? (
                  <Check className="size-4" />
                ) : (
                  <Icon className="size-4" />
                )}
              </div>
              <span
                className={cn(
                  "text-xs font-medium transition-colors",
                  isActive && "text-primary",
                  isDone && !isActive && "text-primary/70",
                  !isActive && !isDone && "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function BookPage() {
  const searchParams = useSearchParams();
  const [step, setStep] = useState(0);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [booking, setBooking] = useState<BookingState>({
    dateRange: { checkIn: null, checkOut: null },
    pricingData: {},
    blockedDates: [],
    guestData: null,
    bookingId: null,
    bookingRef: null,
    orderId: null,
    razorpayKeyId: null,
    advanceAmount: 0,
    totalAmount: 0,
  });

  useEffect(() => {
    const ciParam = searchParams.get("checkIn");
    const coParam = searchParams.get("checkOut");
    if (ciParam) {
      const ci = parseISO(ciParam);
      const co = coParam ? parseISO(coParam) : null;
      if (isValid(ci)) {
        const range: DateRange = {
          checkIn: ci,
          checkOut: co && isValid(co) ? co : null,
        };
        handleDateRangeSelect(range);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateRangeSelect = useCallback(
    async (range: DateRange) => {
      setBooking((prev) => ({ ...prev, dateRange: range }));

      if (range.checkIn && range.checkOut) {
        try {
          const params = new URLSearchParams({
            checkIn: format(range.checkIn, "yyyy-MM-dd"),
            checkOut: format(range.checkOut, "yyyy-MM-dd"),
          });
          const res = await fetch(`/api/availability?${params}`);
          if (res.ok) {
            const data = await res.json();
            const pricingMap: PricingData = {};
            data.pricing?.forEach(
              (p: { date: string; price: number }) =>
                (pricingMap[p.date] = p.price)
            );
            setBooking((prev) => ({
              ...prev,
              pricingData: pricingMap,
              blockedDates: (data.blockedDates || []).map(
                (d: string) => new Date(d)
              ),
            }));
          }
        } catch {
          // availability check failed silently
        }
      }
    },
    []
  );

  const handleGuestSubmit = useCallback(
    async (data: GuestFormData) => {
      if (!booking.dateRange.checkIn || !booking.dateRange.checkOut) return;

      setIsSubmitting(true);
      try {
        const res = await fetch("/api/booking", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            checkIn: format(booking.dateRange.checkIn, "yyyy-MM-dd"),
            checkOut: format(booking.dateRange.checkOut, "yyyy-MM-dd"),
            numGuests: Number(data.guests),
            name: data.name,
            email: data.email,
            phone: data.phone,
            specialRequests: data.specialRequests || "",
          }),
        });

        if (!res.ok) throw new Error("Booking creation failed");

        const bookingRes = await res.json();

        const payRes = await fetch("/api/payment", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bookingId: bookingRes.id,
            amount: bookingRes.advanceAmount,
          }),
        });

        if (!payRes.ok) throw new Error("Payment order creation failed");

        const payData = await payRes.json();

        setBooking((prev) => ({
          ...prev,
          guestData: data,
          bookingId: bookingRes.id,
          bookingRef: bookingRes.bookingRef,
          orderId: payData.orderId,
          razorpayKeyId: payData.key,
          advanceAmount: bookingRes.advanceAmount,
          totalAmount: bookingRes.totalAmount,
        }));

        setStep(2);
      } catch (err) {
        console.error("Booking error:", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [booking.dateRange]
  );

  const handlePaymentSuccess = useCallback(async () => {
    setIsConfirmed(true);
  }, []);

  const canProceedFromDates =
    booking.dateRange.checkIn && booking.dateRange.checkOut;

  const pricing =
    booking.dateRange.checkIn && booking.dateRange.checkOut
      ? calculatePricing(
          booking.dateRange.checkIn,
          booking.dateRange.checkOut,
          booking.pricingData
        )
      : null;

  if (isConfirmed && booking.bookingRef) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-24 pb-16">
          <div className="mx-auto max-w-lg px-4">
            <Card className="border-border/50 bg-card/80 backdrop-blur">
              <CardContent className="pt-8 pb-8">
                <BookingConfirmation
                  referenceNumber={booking.bookingRef}
                  checkIn={booking.dateRange.checkIn!}
                  checkOut={booking.dateRange.checkOut!}
                  guests={Number(booking.guestData?.guests || 1)}
                  amountPaid={booking.advanceAmount}
                  guestEmail={booking.guestData?.email || ""}
                />
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen pt-24 pb-16">
        <div className="mx-auto max-w-3xl px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 text-center"
          >
            <h1 className="font-playfair text-3xl font-bold text-foreground md:text-4xl">
              Book Your Stay
            </h1>
            <p className="mt-2 text-muted-foreground">
              {SITE.name} &mdash; {SITE.tagline}
            </p>
          </motion.div>

          <div className="mb-8">
            <ProgressIndicator currentStep={step} completed={isConfirmed} />
          </div>

          <Card className="border-border/50 bg-card/80 backdrop-blur overflow-hidden">
            <CardContent>
              <AnimatePresence mode="wait">
                {step === 0 && (
                  <motion.div
                    key="dates"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.25 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="font-playfair text-xl font-semibold text-foreground">
                        Select Your Dates
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Choose your check-in and check-out dates
                      </p>
                    </div>

                    <AvailabilityCalendar
                      blockedDates={booking.blockedDates}
                      pricingData={booking.pricingData}
                      onDateRangeSelect={handleDateRangeSelect}
                      selectedRange={booking.dateRange}
                      minNights={1}
                      basePrice={SITE.basePricePerNight}
                    />

                    {pricing && booking.dateRange.checkIn && booking.dateRange.checkOut && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="rounded-xl border border-border bg-secondary/20 p-5"
                      >
                        <PriceBreakdown
                          checkIn={booking.dateRange.checkIn}
                          checkOut={booking.dateRange.checkOut}
                          pricingData={booking.pricingData}
                          basePrice={SITE.basePricePerNight}
                        />
                      </motion.div>
                    )}

                    <div className="flex items-center justify-between pt-2">
                      <Button
                        variant="ghost"
                        asChild
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Link href="/">
                          <ArrowLeft className="size-4" />
                          Back to Home
                        </Link>
                      </Button>

                      <Button
                        onClick={() => setStep(1)}
                        disabled={!canProceedFromDates}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow rounded-xl px-8"
                      >
                        Continue
                        <ArrowRight className="size-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {step === 1 && (
                  <motion.div
                    key="details"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.25 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="font-playfair text-xl font-semibold text-foreground">
                        Guest Details
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {booking.dateRange.checkIn && booking.dateRange.checkOut && (
                          <>
                            {format(booking.dateRange.checkIn, "MMM d")} &ndash;{" "}
                            {format(booking.dateRange.checkOut, "MMM d, yyyy")}
                            {pricing && (
                              <span className="ml-2 font-medium text-primary">
                                {formatINR.format(pricing.total)}
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    </div>

                    <GuestForm
                      onSubmit={handleGuestSubmit}
                      onBack={() => setStep(0)}
                      defaultValues={booking.guestData || undefined}
                      isSubmitting={isSubmitting}
                    />
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="payment"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.25 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="font-playfair text-xl font-semibold text-foreground">
                        Complete Payment
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Secure payment via Razorpay
                      </p>
                    </div>

                    <PaymentStep
                      bookingData={{
                        checkIn: booking.dateRange.checkIn!,
                        checkOut: booking.dateRange.checkOut!,
                        nights: pricing?.nights || 0,
                        guests: Number(booking.guestData?.guests || 1),
                        guestName: booking.guestData?.name || "",
                        guestEmail: booking.guestData?.email || "",
                        guestPhone: booking.guestData?.phone || "",
                        total: booking.totalAmount,
                      }}
                      amount={booking.advanceAmount}
                      razorpayKeyId={booking.razorpayKeyId || ""}
                      orderId={booking.orderId || ""}
                      onSuccess={handlePaymentSuccess}
                      onBack={() => setStep(1)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
}
